import React from "react";
import { toast } from "sonner";
import { X, Copy, User, Sparkles, CalendarDays, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { mediaUrl, fmtUSD, fmtINR } from "@/lib/api";
import ZoomImage from "@/components/ZoomImage";
import { feedSpecs } from "@/lib/useModels";

export default function FeedDetail({ item, onClose }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const recreate = async () => {
    const last = (sessionStorage.getItem("ax_last_studio") || "").split("/")[2];
    let pid = last;
    if (!pid) { const { data } = await api.get("/projects"); pid = data[0]?.id; }
    if (!pid) { toast.error("Create a project first"); return; }
    onClose();
    nav(`/studio/${pid}/${item.section === "blackfx" ? "blackfx" : item.section || "image"}?recreate=${item.id}`);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(item.prompt || ""); toast.success("Prompt copied"); } catch { /* clipboard blocked */ }
  };
  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0A0A0A] border-zinc-800 text-[#F5F5F7] max-w-[1200px] w-[95vw] h-[88vh] p-0 overflow-hidden flex flex-col lg:flex-row [&>button:last-child]:hidden" data-testid="feed-detail">
        {item && (
          <>
            <div className="flex-1 min-h-[40vh] lg:min-h-0 bg-black relative">
              {item.kind === "video"
                ? <video src={mediaUrl(item.url)} autoPlay muted loop playsInline controls className="absolute inset-0 w-full h-full object-contain" data-testid="feed-detail-video" />
                : <div className="absolute inset-0" data-testid="feed-detail-image"><ZoomImage src={mediaUrl(item.url)} alt={item.prompt} /></div>}
            </div>
            <aside className="w-full lg:w-[360px] shrink-0 border-l border-zinc-800 bg-[#0E0E0E] p-6 flex flex-col gap-5 overflow-y-auto">
              <div className="flex items-start justify-between">
                <span className="ax-pill" data-testid="feed-detail-model"><Sparkles className="w-3 h-3" />{item.model_name}</span>
                <button onClick={onClose} className="h-9 w-9 rounded-full bg-[#1a1a1a] hover:bg-[#222] flex items-center justify-center" data-testid="feed-detail-close"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex items-center gap-3" data-testid="feed-detail-creator">
                <div className="h-10 w-10 rounded-full ax-gradient-bg flex items-center justify-center text-black font-bold">{(item.creator || "?")[0].toUpperCase()}</div>
                <div>
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-zinc-500" />{item.creator}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-1.5"><CalendarDays className="w-3 h-3" />{new Date(item.published_at || item.created_at).toLocaleDateString()}</div>
                </div>
              </div>
              {user && (
                <button type="button" onClick={recreate} data-testid="feed-detail-recreate"
                  className="h-11 rounded-xl ax-generate text-sm font-semibold flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" />Recreate with this model</button>
              )}
              <div className="grid grid-cols-2 gap-2 text-[11px] font-mono" data-testid="feed-detail-specs">
                {[["Tool / model", item.model_name], ["Resolution", feedSpecs(item)[0] || item.resolution || "—"], ["Aspect ratio", item.aspect_ratio || "—"],
                  ...(item.cost_usd != null ? [["Cost", `${fmtUSD(item.cost_usd)} · ${fmtINR(item.cost_inr)}`]] : [])].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-zinc-800 bg-[#121214] p-2"><div className="text-zinc-500 uppercase tracking-widest text-[9px]">{k}</div><div className="text-zinc-200 mt-0.5 break-words">{v}</div></div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-zinc-500 uppercase tracking-widest mb-1.5">
                  <span>Prompt</span>
                  <button onClick={copy} className="hover:text-white" data-testid="feed-detail-copy-prompt"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                <p className="text-sm leading-relaxed text-zinc-200 whitespace-pre-wrap" data-testid="feed-detail-prompt">{item.prompt || "—"}</p>
              </div>
            </aside>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
